import { useEffect, useState } from "react";

import { Box, Stack } from "@mui/material";

import useLocalization from "../../hooks/useLocalization";
import useData from "../../hooks/useData";

import Input from "../elements/Input";
import Switch from "../elements/Switch";
import EditorToolbar from "./EditorToolbar";
import Availability from "../ui/Availability";
import Logic from "../ui/Logic";
import Visibility from "../ui/Visibility";

const pageSchema = {
  id: null,
  surveyId: null,
  name: "",
  name_fr: "",
  isActive: true,
  availability: null,
  visibility: null,
  controlledBy: null,
};

const SurveyPageEditor = ({ surveyId, page, onSave, canDelete = false }) => {
  const { getLabel } = useLocalization();
  const { getFromServer, updateRecord, deleteRecord } = useData();

  const [pageData, setPageData] = useState(null);
  const [pages, setPages] = useState([]);

  useEffect(() => {
    if (page) {
      setPageData(page);
    } else {
      setPageData({ ...pageSchema, surveyId });
    }
    getPages();
  }, []);

  const getPages = async () => {
    const surveyPages = await getFromServer({
      entityName: "SurveyPage",
      filter: { surveyId },
    });
    setPages(surveyPages);
  };

  const handleChange = (field, newValue) => {
    setPageData((current) => {
      return { ...current, [field]: newValue };
    });
  };

  const handleSave = async () => {
    let data;
    if (!page) {
      data = { ...pageData, surveyId, pageNumber: pages.length };
    } else {
      data = { ...pageData };
    }
    await updateRecord("SurveyPage", data);
    onSave();
  };

  const handleDelete = async () => {
    if (page?.id) {
      await deleteRecord("SurveyPage", page.id);
      onSave();
    }
  };

  return (
    <Box
      width="100%"
      height="100%"
    >
      <EditorToolbar
        onSave={handleSave}
        onDelete={canDelete ? handleDelete : null}
      />
      <Stack
        spacing={1}
        padding="10px"
      >
        <Input
          id="name"
          label={getLabel("name")}
          value={pageData?.name}
          onChange={(newValue) => handleChange("name", newValue)}
          required
          fullwidth
          multiline
        />
        <Input
          id="name_fr"
          label={getLabel("name_fr")}
          value={pageData?.name_fr}
          onChange={(newValue) => handleChange("name_fr", newValue)}
          required
          fullwidth
          multiline
        />
        <Switch
          label={getLabel("active")}
          value={
            !pageData || pageData.isActive === null ? true : pageData.isActive
          }
          onChange={(newValue) => handleChange("isActive", Boolean(newValue))}
        />
        <Availability
          value={pageData?.availability || []}
          onChange={(newValue) => handleChange("availability", newValue)}
          label={getLabel("availability")}
        />

        <Logic
          entityName="SurveyPage"
          surveyId={surveyId}
          // Ensure backend receives a page with a valid pageNumber when creating
          page={
            pageData?.pageNumber !== undefined
              ? pageData
              : { ...pageData, pageNumber: pages?.length || 0 }
          }
          value={pageData?.controlledBy}
          onChange={(newValue) => handleChange("controlledBy", newValue)}
        />

        <Visibility
          label={getLabel("visibility")}
          value={pageData?.visibility}
          onChange={(newValue) => handleChange("visibility", newValue)}
        />
      </Stack>
    </Box>
  );
};

export default SurveyPageEditor;
